/**
 * Zwölf-Monats-Leiste der Kanalregeln (Board „Vertrieb“): eine Spur je Kanal, Heute-Linie in Wald.
 * Gefüllt heißt bestätigt; offen bernstein, fehlgeschlagen rot gestrichelt – nie gefüllt. Die
 * Leiste beginnt frühestens im laufenden Monat; abgelaufene Regeln stehen in der Liste.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ChannelCloseoutListItem } from '@/shared/api';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { ChannelCloseoutTimelineBar } from './ChannelCloseoutTimelineBar';
import { CLOSEOUT_CHANNELS } from './channelCloseoutPresentation';
import {
  channelBars,
  monthLabel,
  todayOffset,
  type TimelineWindow,
} from './channelCloseoutTimelineModel';

const LANE_HEIGHT = 30;
const LABEL = '7.5rem';

export function ChannelCloseoutTimeline({
  window,
  rules,
  today,
  canShiftBack,
  onSelect,
  onShift,
}: {
  window: TimelineWindow;
  rules: readonly ChannelCloseoutListItem[];
  today: string;
  canShiftBack: boolean;
  /** Ohne Bearbeitungsrecht fehlt er; die Balken bleiben dann reine Anzeige. */
  onSelect?: ((ruleId: string) => void) | undefined;
  onShift: (months: number) => void;
}) {
  const now = todayOffset(window, today);
  return (
    <section
      aria-label="Zeitleiste der Kanalregeln"
      className="bg-paper flex flex-col gap-2 rounded-[18px] px-4 py-3.5"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-ink-soft font-mono text-[11px] font-semibold">
          {monthLabel(window.from)} – {monthLabel(window.to)}
        </p>
        <div className="flex gap-1">
          <ShiftButton
            label="Drei Monate zurück"
            disabled={!canShiftBack}
            onShift={onShift}
            by={-3}
          />
          <ShiftButton label="Drei Monate vor" onShift={onShift} by={3} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="relative min-w-[640px]">
          <div
            className="text-ink-soft grid font-mono text-[10px] font-semibold uppercase tracking-[0.08em]"
            style={{
              marginLeft: LABEL,
              gridTemplateColumns: `repeat(${window.months.length}, minmax(0, 1fr))`,
            }}
          >
            {window.months.map((month) => (
              <span key={month.start} className="border-line border-l pl-1">
                {month.label}
              </span>
            ))}
          </div>
          {CLOSEOUT_CHANNELS.map((channel) => {
            const { bars, lanes } = channelBars(window, rules, channel.code);
            return (
              <div key={channel.code} className="flex items-center py-1">
                <span
                  className="text-ink flex shrink-0 items-center gap-2 text-[13px] font-bold"
                  style={{ width: LABEL }}
                >
                  <OtaLogo channel={channel.code} size={16} />
                  {channel.label}
                </span>
                <div
                  className="bg-card relative flex-1 rounded-lg"
                  style={{ height: lanes * LANE_HEIGHT - 4 }}
                >
                  {bars.map((bar) => (
                    <ChannelCloseoutTimelineBar
                      key={bar.rule.id}
                      rule={bar.rule}
                      // Unter gut einem Monat passt kein lesbarer Name; Tooltip und Liste tragen ihn.
                      showName={bar.width >= 10}
                      onSelect={
                        onSelect && bar.rule.desiredState === 'Active'
                          ? () => onSelect(bar.rule.id)
                          : undefined
                      }
                      style={{
                        left: `${bar.left}%`,
                        width: `max(${bar.width}%, 10px)`,
                        top: 2 + bar.lane * LANE_HEIGHT,
                        height: 22,
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {now !== null && (
            <div
              aria-hidden
              className="border-forest pointer-events-none absolute bottom-0 top-4 border-l-2"
              style={{ left: `calc(${LABEL} + (100% - ${LABEL}) * ${now / 100})` }}
            />
          )}
        </div>
      </div>
      <p className="text-ink-soft text-[11px]">
        <span className="text-forest font-bold">|</span> heute · gefüllt = bestätigt · gestrichelt =
        noch nicht bestätigt · rot gestrichelt = fehlgeschlagen
      </p>
    </section>
  );
}

function ShiftButton({
  label,
  disabled = false,
  by,
  onShift,
}: {
  label: string;
  disabled?: boolean;
  by: number;
  onShift: (months: number) => void;
}) {
  const Icon = by < 0 ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => onShift(by)}
      className="rounded-pill text-ink hover:bg-card grid size-11 place-items-center disabled:pointer-events-none disabled:opacity-40"
    >
      <Icon size={16} aria-hidden />
    </button>
  );
}
